import { DateTime } from "luxon";
import type { Note } from "@/lib/api/notes";
import type { ActionNoteSource } from "../action-analytics";

const EXCLUDED_NOTE_TYPES: ReadonlySet<string> = new Set([
  "escalation",
  "mentor_monthly_update",
]);

export function isDocumentationNote(note: Note): boolean {
  const type = note.metadata?.type;
  return typeof type !== "string" || !EXCLUDED_NOTE_TYPES.has(type);
}

export function resolveNoteSource(note: Note): ActionNoteSource {
  const source = note.metadata?.source;
  const type = note.metadata?.type;
  if (source === "ai" || type === "ai_suggested") return "ai";
  return "mentor";
}

export function filterNotes(notes: Note[], search: string): Note[] {
  const query = search.trim().toLowerCase();
  const documentation = notes.filter(isDocumentationNote);
  if (!query) return documentation;
  return documentation.filter(
    (note) =>
      note.title.toLowerCase().includes(query) ||
      note.content.toLowerCase().includes(query),
  );
}

export function sortNotesByCreatedDesc(notes: Note[]): Note[] {
  return [...notes].sort(
    (a, b) =>
      DateTime.fromISO(b.createdAt).toMillis() -
      DateTime.fromISO(a.createdAt).toMillis(),
  );
}

export function canEditNote(params: {
  note: Note;
  currentUserId: string | undefined;
  canUpdateDocumentation: boolean;
}): boolean {
  const { note, currentUserId, canUpdateDocumentation } = params;
  if (!canUpdateDocumentation) return false;
  if (resolveNoteSource(note) === "ai") return false;
  return Boolean(currentUserId) && note.createdBy?.id === currentUserId;
}

const AUTO_TITLE_WINDOW_MS = 5 * 60 * 1000;

export function resolveDisplayTitle(note: Note): string | null {
  return resolveEditableTitle(note.title, note.createdAt);
}

export function resolveEditableTitle(
  title: string | null | undefined,
  createdAt?: string,
): string | null {
  const trimmed = title?.trim() ?? "";
  if (!trimmed) return null;
  if (createdAt && isAutoGeneratedTitle(trimmed, createdAt)) return null;
  return trimmed;
}

export function persistNoteTitle(title: string | null | undefined): string {
  return title?.trim() ?? "";
}

function isAutoGeneratedTitle(title: string, createdAt: string): boolean {
  const created = DateTime.fromISO(createdAt);
  if (!created.isValid) return false;
  if (title === created.toLocaleString(DateTime.DATETIME_MED)) return true;

  const parsed = DateTime.fromJSDate(new Date(title));
  if (!parsed.isValid) return false;
  return Math.abs(parsed.toMillis() - created.toMillis()) < AUTO_TITLE_WINDOW_MS;
}
